/**
 * Navigation utilities to handle browser navigation and prevent blank pages
 */

export const handleBrowserNavigation = () => {
  // Store current page state
  const currentState = {
    pathname: window.location.pathname,
    timestamp: Date.now(),
    scrollY: window.scrollY
  };

  // Store in session storage for recovery
  sessionStorage.setItem('navigationState', JSON.stringify(currentState));

  // Listen for popstate events (back/forward button)
  const handlePopState = (event: PopStateEvent) => {
    const storedState = sessionStorage.getItem('navigationState');
    
    if (storedState) {
      const state = JSON.parse(storedState);
      
      // If we're navigating to a different page, ensure proper loading
      if (state.pathname !== window.location.pathname) {
        // Force a small delay to ensure React Router has time to update
        setTimeout(() => {
          window.scrollTo(0, 0);
          
          // Trigger a custom event to notify components
          window.dispatchEvent(new CustomEvent('navigationChange', {
            detail: { pathname: window.location.pathname }
          }));
        }, 50);
      }
    }
  };

  window.addEventListener('popstate', handlePopState);

  // Cleanup function
  return () => {
    window.removeEventListener('popstate', handlePopState);
  };
};

export const preventBlankPage = () => {
  // Check if page is blank (no content rendered)
  const checkForBlankPage = () => {
    const body = document.body;
    const hasContent = body.children.length > 0 && 
                      body.innerHTML.trim().length > 0 &&
                      !body.innerHTML.includes('Loading...') &&
                      document.readyState === 'complete';

    if (!hasContent) {
      console.warn('Blank page detected, attempting recovery...');
      
      // Try to recover by reloading the page
      setTimeout(() => {
        if (document.readyState === 'complete' && body.children.length === 0) {
          window.location.reload();
        }
      }, 1000);
    }
  };

  // Check after page load
  if (document.readyState === 'complete') {
    checkForBlankPage();
  } else {
    window.addEventListener('load', checkForBlankPage);
  }

  // Also check after a delay to catch late-loading issues
  setTimeout(checkForBlankPage, 2000);
};

export const enhanceNavigation = () => {
  // Add navigation enhancement to all pages
  const cleanup = handleBrowserNavigation();
  preventBlankPage();

  // Listen for navigation changes
  window.addEventListener('navigationChange', (event: any) => {
    console.log('Navigation changed to:', event.detail.pathname);
    
    // Ensure proper page title
    const pathname = event.detail.pathname;
    switch (pathname) {
      case '/':
        document.title = 'Accueil | Kissaria';
        break;
      case '/cart':
        document.title = 'Panier | Kissaria';
        break;
      case '/checkout':
        document.title = 'Commande | Kissaria';
        break;
      case '/shop':
        document.title = 'Boutique | Kissaria';
        break;
      default:
        document.title = 'Kissaria';
    }
  });

  return cleanup;
};

export const safeNavigate = (navigate: any, path: string) => {
  try {
    // Store current state before navigation
    const currentState = {
      pathname: window.location.pathname,
      timestamp: Date.now()
    };
    sessionStorage.setItem('previousState', JSON.stringify(currentState));
    
    // Navigate to new path
    navigate(path);
    
    // Ensure proper scroll position
    setTimeout(() => {
      window.scrollTo(0, 0);
    }, 100);
  } catch (error) {
    console.error('Navigation error:', error);
    // Fallback to window.location
    window.location.href = path;
  }
}; 